import './MembershipCardPreview.css';
import { membershipCardSurface, resolveThemeToken } from '../themeTokens';

type MembershipCardPreviewProps = {
  name: string;
  description: string;
  tierLevel: number;
  pricePaise: number;
  validityDays: number;
  themeColorToken: string;
  perks: string[];
  readinessItems?: Array<{ label: string; complete: boolean }>;
};

const TIER_LABELS: Record<number, string> = {
  1: 'Entry',
  2: 'Mid',
  3: 'Top',
  4: 'Elite'
};

const formatRupeesFromPaise = (value: number): string => `₹${(Math.max(value || 0, 0) / 100).toLocaleString('en-IN')}`;

const MembershipCardPreview = ({
  name,
  description,
  tierLevel,
  pricePaise,
  validityDays,
  themeColorToken,
  perks,
  readinessItems = []
}: MembershipCardPreviewProps) => {
  const theme = resolveThemeToken(themeColorToken);
  // The customer card is always a dark base with the tier colour tinted in from
  // the top (matches the mobile app), so text is always light and the accent is
  // the tier accent.
  const previewText = '#FFFFFF';
  const previewMutedText = 'rgba(241, 245, 249, 0.78)';
  const previewAccent = theme.accentHex;
  const previewGlow = theme.accentRing;
  const tierBadgeBackground = 'rgba(255, 255, 255, 0.10)';
  const tierBadgeBorder = 'rgba(255, 255, 255, 0.14)';
  const tierValue = tierLevel || 1;
  const tierLabel = TIER_LABELS[tierValue] || `Tier ${tierValue}`;
  const priceLabel = (pricePaise || 0) === 0 ? 'Free' : formatRupeesFromPaise(pricePaise || 0);
  const previewPerks = perks.filter((perk) => perk.trim().length > 0).slice(0, 3);
  const hasName = name.trim().length > 0;
  const previewName = name.trim() || 'Membership name';
  const previewDescription = description.trim() || 'Membership description will appear here.';
  const previewValidity = (validityDays || 0) >= 36500 ? 'Lifetime' : `${validityDays || 0} days`;

  return (
    <section className="membership-preview-rail appointments-v2-card">
      <div className="appointments-v2-card-header membership-preview-rail__header">
        <div>
          <p className="membership-preview-rail__eyebrow">Live Preview</p>
          <h3>Customer membership card</h3>
        </div>
      </div>

      <div className="appointments-v2-card-body membership-preview-rail__body">
        <article
          className={`membership-preview-card${hasName ? '' : ' is-empty'}`}
          style={{
            background: membershipCardSurface(theme),
            color: previewText,
            ['--membership-preview-text-muted' as string]: previewMutedText,
            ['--membership-preview-accent' as string]: previewAccent,
            ['--membership-preview-glow' as string]: previewGlow,
            ['--membership-preview-badge-bg' as string]: tierBadgeBackground,
            ['--membership-preview-badge-border' as string]: tierBadgeBorder
          }}
        >
          <div className="membership-preview-card__halo" />
          <div className="membership-preview-card__content">
            <div className="membership-preview-card__tier-row">
              <span className="membership-preview-card__tier-badge">{tierLabel}</span>
              <span className="membership-preview-card__tier-level">Tier {tierValue}</span>
            </div>

            <div className="membership-preview-card__identity">
              {hasName ? (
                <>
                  <h4>{previewName}</h4>
                  <p>{previewDescription}</p>
                </>
              ) : (
                <div className="membership-preview-card__emptyState">
                  <h4>Name your membership</h4>
                  <p>Start with a clear tier name to see the card come alive for merchants and customers.</p>
                </div>
              )}
            </div>

            <div className="membership-preview-card__footer">
              <div>
                <strong>{priceLabel}</strong>
                <span>{(pricePaise || 0) === 0 ? 'Free tier' : 'Membership fee'}</span>
              </div>
              <div>
                <strong>{previewValidity}</strong>
                <span>Validity</span>
              </div>
            </div>
          </div>
        </article>

        <section className="membership-preview-panel">
          {readinessItems.length > 0 ? (
            <div className="membership-preview-panel__section">
              <p className="membership-preview-panel__label">Ready to publish?</p>
              <ul className="membership-preview-panel__readiness">
                {readinessItems.map((item) => (
                  <li key={item.label} className={item.complete ? 'is-complete' : 'is-pending'}>
                    <span className="membership-preview-panel__readiness-dot" />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="membership-preview-panel__section">
            <p className="membership-preview-panel__label">Visible perks</p>
            {previewPerks.length > 0 ? (
              <div className="membership-preview-panel__perk-list">
                {previewPerks.map((perk, index) => (
                  <span key={`preview-perk-${index}`} className="membership-preview-panel__perk-pill">{perk}</span>
                ))}
              </div>
            ) : (
              <p className="membership-preview-panel__empty">Add at least one perk to make the card feel complete.</p>
            )}
          </div>

          <div className="membership-preview-panel__section">
            <p className="membership-preview-panel__label">Preview notes</p>
            <ul className="membership-preview-panel__notes">
              <li>Theme swatches control the card finish merchants will recognize in the dashboard.</li>
              <li>Price and validity stay visually secondary to the membership name and tier.</li>
              <li>Only the first few perks are surfaced on the compact card.</li>
            </ul>
          </div>
        </section>
      </div>
    </section>
  );
};

export default MembershipCardPreview;
